// Lesson_QuadraticGeneral_TeoriaPrzyklady.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import ParabolaShape from "../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../components/visuals/NumberLineRanges";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";

/** Delikatny separator przerywany (dopasowuje się do motywu przez currentColor) */
function DashedSep({ margin = "0.75rem" }) {
  return (
    <hr
      role="separator"
      aria-hidden="true"
      style={{
        border: 0,
        borderTop: "1px dashed",
        borderColor: "var(--color-text)",
        opacity: 1,
        margin: `${margin} 0`,
      }}
    />
  );
}

/** Pomocniczo: klasyczny zapis ax^2 + bx + c (z ładnym ±1 przy a) */
function formatQuadraticLatex(a, b, c) {
  const term = (coeff, base, omitOne=false) => {
    if (coeff === 0) return "";
    const sign = coeff > 0 ? " + " : " - ";
    const abs = Math.abs(coeff);
    const num = (abs === 1 && omitOne) ? "" : String(abs);
    return `${sign}${num}${base}`;
  };
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2${term(b,"x",true)}`;
  if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
  return out;
}

export default function Lesson_QuadraticGeneral_TeoriaPrzyklady() {
  const intro = (
    <div className="space-y-3">
      <p>
        <strong>Postać ogólna</strong> funkcji kwadratowej to{" "}
        <InlineMath math={'f(x)=ax^2+bx+c'} />. Współczynnik
        <InlineMath math={'\\ a'} /> decyduje o kierunku ramion paraboli
        (gdy <InlineMath math={'a>0'} /> — do góry, gdy <InlineMath math={'a<0'} /> — w dół),
        <InlineMath math={'\\ c=f(0)'} /> to punkt przecięcia z osią OY, a
        o liczbie miejsc zerowych decyduje{" "}
        <InlineMath math={'\\Delta=b^2-4ac'} />.
      </p>

      <div className="text-center space-y-1">
        <p className="font-medium">Kierunek ramion a znak <InlineMath math={'\\Delta'} /> — przegląd przypadków</p>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <p className="text-sm"><InlineMath math={'a>0,\\ \\Delta>0'} /> — dwa zera</p>
            <ParabolaShape aSign={1} xRange={5} yWindow={5} showXAxis showZeroTicks rootMode="two" rootLeft={-2} rootRight={2} curveColor="#f59e0b" />
          </div>
          <div>
            <p className="text-sm"><InlineMath math={'a>0,\\ \\Delta=0'} /> — styczność</p>
            <ParabolaShape aSign={1} xRange={5} yWindow={5} showXAxis showZeroTicks rootMode="one" tangentX={1} curveColor="#f59e0b" />
          </div>
          <div>
            <p className="text-sm"><InlineMath math={'a>0,\\ \\Delta<0'} /> — brak zer</p>
            <ParabolaShape aSign={1} xRange={5} yWindow={5} showXAxis showZeroTicks rootMode="none" curveColor="#f59e0b" />
          </div>
        </div>
      </div>

      <DashedSep />

      <p>
        <strong>Wierzchołek</strong> paraboli ma współrzędne{" "}
        <InlineMath math={'(p,\\ q)'} />, gdzie{" "}
        <InlineMath math={'p=-\\dfrac{b}{2a}'} /> i{" "}
        <InlineMath math={'q=-\\dfrac{\\Delta}{4a}'} />. Miejsca zerowe (gdy istnieją) liczymy ze wzoru
        <InlineMath math={'\\ x_{1,2}=\\dfrac{-b\\pm\\sqrt{\\Delta}}{2a}'} />.
      </p>
    </div>
  );

  const examples = [
    /* 1) a,b,c  — FindQuadraticCoefficientsTask */
    {
      label: "Przykład 1",
      summary: "Odczytaj współczynniki a, b, c z postaci ogólnej",
      description: "Rozpoznawanie współczynników.",
      content: (
        <div className="space-y-3">
          <p><strong>Polecenie:</strong> Dla funkcji <InlineMath math={'f(x) = ' + formatQuadraticLatex(-2, 5, -3)} /> odczytaj <InlineMath math={'a,\\ b,\\ c'} />.</p>

          <p><strong>Krok 1.</strong> Porównujemy z <InlineMath math={'ax^2+bx+c'} />.</p>
          <BlockMath math={'a=-2,\\quad b=5,\\quad c=-3'} />

          <p><strong>Wniosek:</strong> <InlineMath math={'a=-2,\\ b=5,\\ c=-3'} />.</p>
        </div>
      ),
    },

    /* 2) wierzchołek  — FindVertexFromGeneralTask */
    {
      label: "Przykład 2",
      summary: "Wierzchołek W(p, q) z postaci ogólnej",
      description: "Korzystamy ze wzorów p = -b/(2a), q = -Δ/(4a).",
      content: (
        <div className="space-y-3">
          <p><strong>Polecenie:</strong> Wyznacz <InlineMath math={'W(p,\\ q)'} /> dla <InlineMath math={'f(x) = ' + formatQuadraticLatex(1, -4, 1)} />.</p>

          <p><strong>Krok 1.</strong> <InlineMath math={'p = -\\dfrac{b}{2a} = -\\dfrac{-4}{2\\cdot 1} = 2'} /></p>
          <p><strong>Krok 2.</strong> <InlineMath math={'\\Delta = (-4)^2 - 4\\cdot 1 \\cdot 1 = 16-4=12'} /></p>
          <p><strong>Krok 3.</strong> <InlineMath math={'q = -\\dfrac{\\Delta}{4a} = -\\dfrac{12}{4\\cdot 1} = -3'} /></p>

          <p><strong>Wniosek:</strong> <InlineMath math={'W=(2,\\ -3)'} />.</p>

          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <ParabolaShape
              aSign={1}
              xRange={6}
              yWindow={6}
              showXAxis
              showZeroTicks
              rootMode="two"
              rootLeft={2 - Math.sqrt(12)/2}
              rootRight={2 + Math.sqrt(12)/2}
              tangentX={2}
              vertex={{ x: 2, y: -3 }}
              curveColor="#f59e0b"
            />
          </div>
        </div>
      ),
    },

    /* 3) Δ i liczba miejsc zerowych  — DeltaAndZerosCountTask */
    {
      label: "Przykład 3",
      summary: "Delta i liczba miejsc zerowych",
      description: "Liczymy Δ i wnioskujemy: 0 / 1 / 2 miejsc zerowych.",
      content: (
        <div className="space-y-3">
          <p><strong>Polecenie:</strong> Dla <InlineMath math={'f(x) = ' + formatQuadraticLatex(2, 1, 3)} /> oblicz <InlineMath math={'\\Delta'} /> i podaj liczbę miejsc zerowych.</p>

          <p><strong>Krok 1.</strong> <InlineMath math={'\\Delta = 1^2 - 4\\cdot 2 \\cdot 3 = 1-24 = -23'} /></p>
          <p><strong>Krok 2.</strong> Skoro <InlineMath math={'\\Delta<0'} />, to <strong>brak miejsc zerowych</strong>.</p>

          <p><strong>Wniosek:</strong> <InlineMath math={'\\Delta=-23'} />, liczba miejsc zerowych: <InlineMath math={'0'} />.</p>
        </div>
      ),
    },

    /* 4) x1, x2  — FindRootsFromGeneralTask */
    {
      label: "Przykład 4",
      summary: "Miejsca zerowe z wykorzystaniem wzorów kwadratowych",
      description: "Przypadek z Δ > 0.",
      content: (
        <div className="space-y-3">
          <p><strong>Polecenie:</strong> Znajdź <InlineMath math={'x_1,\\ x_2'} /> dla <InlineMath math={'f(x) = ' + formatQuadraticLatex(1, -1, -6)} />.</p>

          <div style={{ textAlign: "center" }}>
            <BlockMath math={
              String.raw`\begin{aligned}
              a&=1,\quad b=-1,\quad c=-6\\
              \Delta&=(-1)^2-4\cdot 1\cdot(-6)=1+24=25\\
              x_{1,2}&=\dfrac{-b\pm\sqrt{\Delta}}{2a}=\dfrac{1\pm 5}{2}
              \end{aligned}`
            } />
          </div>

          <p><strong>Wniosek:</strong> <InlineMath math={'x_1=-2,\\ x_2=3'} />.</p>

          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <NumberLineRanges
              lockPair
              showZero
              ranges={[
                { to: -2, rightClosed: true, rightLabelLatex: "-2" },
                { from: 3, leftClosed: true, leftLabelLatex: "3" },
              ]}
            />
          </div>
        </div>
      ),
    },

    /* 5) x1, x2, W(p,q), y0 + wykres  — PlotQuadraticSpecialPointsTask */
    {
      label: "Przykład 5",
      summary: "Komplet charakterystycznych punktów i szkic wykresu",
      description: "Wyznaczamy x1, x2, wierzchołek W(p, q) i punkt OY (y0 = c).",
      content: (
        <div className="space-y-3">
          <p>
            <strong>Polecenie:</strong> Dla <InlineMath math={'f(x) = ' + formatQuadraticLatex(1, -4, -5)} /> oblicz
            <InlineMath math={'\\ x_1,\\ x_2,\\ W(p,\\ q),\\ y_0'} /> oraz narysuj szkic wykresu.
          </p>

          <div style={{ textAlign: "center" }}>
            <BlockMath math={
              String.raw`\begin{aligned}
              \Delta&=(-4)^2-4\cdot 1\cdot(-5)=16+20=36,\ \sqrt{\Delta}=6\\
              x_1&=\dfrac{-(-4)-6}{2\cdot 1}=\dfrac{4-6}{2}=-1,\qquad
              x_2=\dfrac{4+6}{2}=5\\[2pt]
              p&=-\dfrac{b}{2a}=-\dfrac{-4}{2}=2,\qquad
              q=-\dfrac{\Delta}{4a}=-\dfrac{36}{4}=-9\\
              y_0&=f(0)=c=-5
              \end{aligned}`
            } />
          </div>

          <p><strong>Wynik:</strong> <InlineMath math={'x_1=-1,\\ x_2=5,\\ W=(2,\\ -9),\\ y_0=-5'} />.</p>

          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <GraphViewer
              points={{
                X1: { x: -1, y: 0, label: "(-1; 0)", color: "#FFA500" },
                X2: { x: 5,  y: 0, label: "(5; 0)",  color: "#FFA500" },
                W:  { x: 2,  y: -9, label: "W (2; -9)", color: "#FFA500" },
                Y0: { x: 0,  y: -5, label: "(0; -5)", color: "#FFA500" },
              }}
              equations={[
                { expr: `y = 1 * x * x - 4 * x - 5`, color: "#2e7d32", label: "parabola" }
              ]}
              xRange={[-8, 8]}
              yRange={[-10, 6]}
              showGrid
              width={520}
              height={520}
            />
          </div>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
